import { agoLabel, heartbeatHealth, intervalLabel } from "../lib/heartbeat.ts";
import type { PingResponse } from "../lib/types.ts";
import { type Status, StatusBadge } from "./StatusBadge.tsx";

interface PingPanelProps {
  /** The last successful ping, or null if none has come back yet. */
  data: PingResponse | null;
  /** Message from the last failed ping, or null. */
  error: string | null;
  /** A request is in flight. */
  pending: boolean;
  onPing: () => void;
  /** Injected so the relative labels are testable and re-render on a tick. */
  now: Date;
}

/** Presentational — it decides nothing but how to say what it was handed. */
export function PingPanel({ data, error, pending, onPing, now }: PingPanelProps) {
  const apiStatus: Status = error
    ? "unreachable"
    : !data
      ? "checking"
      : data.status === "ok"
        ? "ok"
        : "degraded";

  const last = data?.heartbeat.last ?? null;
  const health = data ? heartbeatHealth(last, data.heartbeat.intervalMs, now) : "checking";

  return (
    <section className="flex flex-col gap-5 rounded-xl border border-line bg-surface-raised p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="font-medium">Backend</h2>
          <p className="font-mono text-xs text-ink-muted">GET /api/ping</p>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge
            status={apiStatus}
            label={
              apiStatus === "unreachable"
                ? "unreachable"
                : apiStatus === "checking"
                  ? "checking…"
                  : apiStatus === "degraded"
                    ? "degraded"
                    : "reachable"
            }
          />
          <button
            type="button"
            onClick={onPing}
            disabled={pending}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white
              transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {pending ? "Pinging…" : "Ping"}
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
          {error} — is the backend running on :3001?
        </p>
      )}

      {data && (
        <dl className="flex flex-col gap-3 border-t border-line pt-4 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-ink-muted">Server time</dt>
            <dd className="font-mono tabular-nums">{new Date(data.time).toLocaleTimeString()}</dd>
          </div>

          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-ink-muted">Scheduled job</dt>
            <dd>{intervalLabel(data.heartbeat.intervalMs)}</dd>
          </div>

          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-ink-muted">Last heartbeat</dt>
            <dd className="flex items-center gap-3">
              {last ? (
                <>
                  <time dateTime={last.ranAt} className="font-mono tabular-nums">
                    {new Date(last.ranAt).toLocaleTimeString()}
                  </time>
                  <span className="text-ink-muted">{agoLabel(last.ranAt, now)}</span>
                </>
              ) : (
                <span className="text-ink-muted">none yet</span>
              )}
              <StatusBadge
                status={health === "checking" ? "checking" : health}
                label={
                  health === "waiting"
                    ? "waiting for the first tick"
                    : health === "late"
                      ? "late"
                      : "on time"
                }
              />
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}
